import { useMemo } from "react";
import { OPERATION_LABEL, incidentLabel, type Incident, type Machine, type TaskWithJob } from "../data/types.ts";
import { fmtDateTime, fmtDuration, fmtTime, minutesSince, startOfDay } from "../lib/time.ts";
import { Badge, Empty, ErrorBox, Loading, useApp, useCountUp, useData, useNow } from "../ui/kit.tsx";
import { Header } from "./Planning.tsx";

type MState = { label: string; tone: string };

function machineState(current: TaskWithJob | undefined, incident: Incident | undefined): MState {
  if (incident) return { label: `Aléa · ${incidentLabel(incident.kind)}`, tone: "orange" };
  if (!current) return { label: "À l'arrêt", tone: "" };
  if (current.status === "calage") return { label: "En calage", tone: "blue" };
  if (current.status === "pause") return { label: "En pause", tone: "" };
  return { label: "En production", tone: "teal" };
}

export function LivePage() {
  const { api } = useApp();
  const now = useNow(15_000);
  const today = useMemo(() => startOfDay(new Date()), []);
  const { data, error, loading } = useData(api, async () => {
    const [machines, tasks, incidents] = await Promise.all([api.machines(), api.tasks(today), api.incidents(today)]);
    return { machines: machines.filter((m) => m.active), tasks, incidents };
  }, [today], 8_000);
  if (error) return <ErrorBox message={error} />;
  if (loading || !data) return <Loading />;
  const { machines, tasks, incidents } = data;
  if (!machines.length) return <div className="page"><Header title="Atelier en direct" /><Empty title="Aucune machine paramétrée" /></div>;

  const todayIncidents = [...incidents].sort((a, b) => b.started_at.localeCompare(a.started_at));
  const lost = todayIncidents.reduce((a, i) => a + minutesSince(i.started_at, i.ended_at ? new Date(i.ended_at) : now), 0);
  const done = tasks.filter((t) => t.status === "termine" && t.finished_at && new Date(t.finished_at) >= today);
  const setup = done.reduce((a, t) => a + (t.setup_min || 0), 0);

  return (
    <div className="page">
      <Header title="Atelier en direct"><span className="live-tag"><i className="dot dot-teal" aria-hidden="true" />Mis à jour à {fmtTime(now)}</span></Header>
      <div className="kpi-strip card">
        <Kpi label="Jobs terminés aujourd'hui" value={done.length} />
        <Kpi label="Temps perdu en aléas" value={lost} format={fmtDuration} />
        <Kpi label="Calages réalisés" value={done.filter((t) => (t.setup_min || 0) > 0).length} sub={`${fmtDuration(setup)} planifiées`} />
        <Kpi label="Machines en aléa" value={incidents.filter((i) => !i.ended_at).length} warn={incidents.some((i) => !i.ended_at)} />
      </div>
      <div className="machines-grid">
        {machines.map((m) => <MachineCard key={m.id} m={m} tasks={tasks} incidents={incidents} now={now} today={today} />)}
      </div>
      <section className="card pad-l inc-journal">
        <div className="ij-head"><h2>Aléas du jour</h2>{todayIncidents.length > 0 && <span className="muted">{todayIncidents.length} signalé{todayIncidents.length > 1 ? "s" : ""} · {fmtDuration(lost)} perdues</span>}</div>
        {!todayIncidents.length ? <p className="muted ij-empty">Aucun aléa signalé aujourd'hui. Les conducteurs les déclarent d'un geste depuis leur tablette.</p> : (
          <div className="table-wrap"><table className="table">
            <thead><tr><th>Heure</th><th>Machine</th><th>Aléa</th><th>Job</th><th className="num">Durée</th></tr></thead>
            <tbody>
              {todayIncidents.map((i) => {
                const t = tasks.find((x) => x.id === i.task_id);
                return (
                  <tr key={i.id}>
                    <td>{fmtTime(i.started_at)}</td>
                    <td>{machines.find((m) => m.id === i.machine_id)?.name || "—"}</td>
                    <td>{incidentLabel(i.kind)}{i.comment && <div className="muted small">{i.comment}</div>}</td>
                    <td>{t ? `${t.job.of_number} · ${t.job.client}` : "—"}</td>
                    <td className="num">{i.ended_at ? fmtDuration(minutesSince(i.started_at, new Date(i.ended_at))) : <Badge tone="orange">en cours · {fmtDuration(minutesSince(i.started_at, now))}</Badge>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table></div>
        )}
      </section>
    </div>
  );
}

function Kpi({ label, value, format, sub, warn }: { label: string; value: number; format?: (n: number) => string; sub?: string; warn?: boolean }) {
  const n = useCountUp(value);
  return <div className="kpi"><span>{label}</span><b className={warn ? "warn" : ""}>{format ? format(n) : n}</b>{sub && <em>{sub}</em>}</div>;
}

const when = (iso: string, today: Date) => (new Date(iso) < new Date(today.getTime() + 86_400_000) ? `à ${fmtTime(iso)}` : fmtDateTime(iso));

function MachineCard({ m, tasks, incidents, now, today }: { m: Machine; tasks: TaskWithJob[]; incidents: Incident[]; now: Date; today: Date }) {
  const current = tasks.filter((t) => t.machine_id === m.id && ["calage", "production", "pause"].includes(t.status))
    .sort((a, b) => (b.started_at || "").localeCompare(a.started_at || ""))[0];
  const incident = incidents.find((i) => i.machine_id === m.id && !i.ended_at);
  const next = tasks.filter((t) => t.pub_machine_id === m.id && (t.status === "planifie" || t.status === "a_planifier") && t.pub_start)
    .sort((a, b) => a.pub_start!.localeCompare(b.pub_start!))[0];
  const st = machineState(current, incident);
  const pct = current ? Math.min(100, Math.round((current.produced_qty / Math.max(1, current.job.quantity)) * 100)) : 0;
  const late = current && current.planned_end && new Date(current.planned_end) < now;
  const doneToday = tasks.filter((t) => t.machine_id === m.id && t.status === "termine" && t.finished_at && new Date(t.finished_at) >= today).length;
  const lostToday = incidents.filter((i) => i.machine_id === m.id).reduce((a, i) => a + minutesSince(i.started_at, i.ended_at ? new Date(i.ended_at) : now), 0);
  return (
    <div className={`card mcard tone-${st.tone || "idle"}`}>
      <div className="mcard-head"><b>{m.name}</b><span className={`mstate ms-${st.tone || "idle"}`}><i className={`dot dot-${st.tone || "idle"}`} aria-hidden="true" />{st.label}</span></div>
      {incident && <div className="mcard-incident">Arrêtée depuis {fmtDuration(minutesSince(incident.started_at, now))}{incident.comment ? ` · ${incident.comment}` : ""}</div>}
      {current ? (
        <>
          <div className="mcard-job"><span className="mj-of">{current.job.of_number}</span><b>{current.job.client}</b><div className="muted small">{OPERATION_LABEL[current.operation]} {current.format_in} → {current.format_out} · {current.job.title}</div></div>
          <div className="mcard-prog">
            <div className="progress"><i style={{ width: `${pct}%` }} /></div>
            <b>{pct}&nbsp;%</b>
          </div>
          <div className="mcard-foot">
            <span>{current.produced_qty.toLocaleString("fr-FR")} / {current.job.quantity.toLocaleString("fr-FR")} ex.</span>
            <span className={late ? "late" : ""}>{current.planned_end ? `${late ? "devait finir" : "fin prévue"} ${fmtTime(current.planned_end)}` : ""}</span>
          </div>
        </>
      ) : (
        <div className="mcard-idle">{next ? <>Prochain job <b>{next.job.of_number}</b> {next.job.client}, {when(next.pub_start!, today)}</> : "Rien de publié pour cette machine."}</div>
      )}
      {current && next && <div className="mcard-next">Ensuite <b>{next.job.of_number}</b> {next.job.client}, {when(next.pub_start!, today)}</div>}
      <div className="mcard-day">Aujourd'hui : {doneToday} job{doneToday > 1 ? "s" : ""} terminé{doneToday > 1 ? "s" : ""}{lostToday ? ` · ${fmtDuration(lostToday)} d'aléas` : " · aucun aléa"}</div>
    </div>
  );
}
